import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";

const CONTACT_EMAIL = "admin@whatsyourbalance.com";
const EFFECTIVE_DATE = "February 27, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen" data-testid="marketing-terms">
      <MarketingHeader />
      <section className="max-w-3xl mx-auto px-6 md:px-8 py-20">
        <div className="mb-10">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Legal</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#2C302E]">
            Terms of Service
          </h1>
          <p className="text-sm text-[#8A918D] mt-2">Effective {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-neutral max-w-none space-y-8 text-[#2C302E] leading-relaxed">
          <div className="rounded-2xl border border-[#D8CFB9] bg-[#FBF7F0] p-4 text-sm text-[#59605C]">
            What&rsquo;s Your Balance? is currently in a private beta. These terms describe how you and we agree to use the
            beta while it is being tested. They will be updated before a public launch.
          </div>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">1. What this service is</h2>
            <p>
              What&rsquo;s Your Balance? (&ldquo;the service&rdquo;) is a personal cash-flow forecasting tool. You manually enter your
              account balances, bills, and income, and the service shows you a projection of whether your paycheck is
              likely to cover your obligations. It is not a bank, not a payment service, and not a licensed financial advisor.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">2. Not financial advice</h2>
            <p>
              Nothing shown in the service is financial, tax, investment, legal, or accounting advice. Projections are based
              solely on the data you enter and simple math. You are responsible for verifying your real bank balances,
              actual bills, and any decisions you make with your money. Do not rely on the service alone to make important
              financial decisions.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">3. Your account</h2>
            <p>
              You need an account to use the service. Keep your password confidential. You are responsible for activity on
              your account. If you sign in with Google, that authentication is handled by Google under their terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">4. Your data</h2>
            <p>
              You own the data you enter. We use it only to run the service for you. We do not sell your data. See the
              <a href="/privacy" className="text-[#4A6148] underline"> Privacy Policy </a>
              for how data is stored, secured, and can be deleted.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">5. Manual entry &amp; accuracy</h2>
            <p>
              The service does not connect to your bank. You enter balances and bills yourself. If your inputs are wrong,
              your projections will be wrong. Keep balances up to date for the forecast to reflect reality.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">6. Beta status &amp; changes</h2>
            <p>
              The service is provided during beta &ldquo;as is&rdquo; without warranty of any kind. Features may change, break,
              or be removed. We may pause the service, change pricing, or discontinue it. We will make reasonable efforts
              to notify active users of material changes.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">7. Subscriptions &amp; trials</h2>
            <p>
              Payment processing is handled by Stripe under their terms. New accounts start with a 30-day free trial. If
              you subscribe, you can cancel any time in the Subscription page; access continues until the end of the paid
              period. Refunds are handled on a case-by-case basis via the contact address below.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">8. Acceptable use</h2>
            <p>
              Do not attempt to break the service, access other users&rsquo; data, submit unlawful content, scrape the site
              in bulk, or use the service to distribute spam, malware, or abusive content. We may suspend accounts that
              violate these rules.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">9. Limitation of liability</h2>
            <p>
              To the extent allowed by law, the service is provided without warranties, and we are not liable for any
              indirect, incidental, or consequential damages, or for any financial loss resulting from decisions you make
              using the service. Our total liability for any claim will not exceed the amount you paid us in the previous
              12 months.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">10. Termination</h2>
            <p>
              You can delete your account any time by contacting us. We can suspend or end access if you violate these
              terms. On termination, your projections and data become inaccessible; contact us before deletion if you want
              an export.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">11. Governing law</h2>
            <p>
              These terms are governed by the laws of the state in which the service operator resides, without regard to
              conflict-of-law rules. Disputes will be resolved in the courts located there.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-[#2C302E] mb-2">12. Contact</h2>
            <p>
              Questions or concerns? Email
              {" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#4A6148] underline">{CONTACT_EMAIL}</a>.
            </p>
          </section>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
